import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { embeddedAuthConfig, BASE_API_URL } from '../../config';
import {
  buildAuthorizeUrl,
  buildLogoutUrl,
  createPkcePair,
  EmbeddedAuthConfig,
  exchangeAuthorizationCode,
  randomString,
  refreshEnterpriseTokens,
  refreshPasswordlessTokens,
  startPasswordless,
  TokenSet,
  verifyPasswordlessOtp,
} from './authApi';

const TOKEN_STORAGE_KEY = 'embedded_auth_tokens';
const TRANSACTION_STORAGE_KEY = 'embedded_auth_tx';
const EXPIRY_LEEWAY_MS = 60_000;
const DEFAULT_EXTERNAL_LIFETIME_MS = 60 * 60 * 1000;

const decodeJwtClaims = (jwt: string): Record<string, unknown> | null => {
  try {
    const segment = jwt.split('.')[1];
    if (!segment) return null;
    const payload = segment.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
};

const decodeExpiry = (jwt: string): number => {
  const claims = decodeJwtClaims(jwt);
  if (claims && typeof claims.exp === 'number') {
    return claims.exp * 1000;
  }
  return Date.now() + DEFAULT_EXTERNAL_LIFETIME_MS;
};

interface RedirectTransaction {
  verifier: string;
  state: string;
  nonce: string;
  connection: string;
  redirectUri: string;
}

export interface EmbeddedAuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | null;
  startPasswordless: (email: string) => Promise<void>;
  verifyOtp: (email: string, otp: string) => Promise<void>;
  loginWithConnection: (connection: string) => Promise<void>;
  handleRedirectCallback: () => Promise<void>;
  getAccessToken: () => Promise<string | undefined>;
  logout: (options?: { returnTo?: string }) => void;
}

export const EmbeddedAuthContext =
  createContext<EmbeddedAuthContextValue | null>(null);

const readStoredTokens = (): TokenSet | null => {
  const raw = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as TokenSet;
  } catch {
    return null;
  }
};

const readHashToken = (): TokenSet | null => {
  const hashToken = new URLSearchParams(
    window.location.hash.replace('#', '')
  ).get('token');
  if (!hashToken) return null;
  return {
    accessToken: hashToken,
    expiresAt: decodeExpiry(hashToken),
    source: 'external',
  };
};

const readInitialTokens = (): TokenSet | null =>
  readHashToken() ?? readStoredTokens();

const config: EmbeddedAuthConfig = {
  domain: embeddedAuthConfig.domain,
  clientId: embeddedAuthConfig.clientId,
  audience: embeddedAuthConfig.audience,
  scope: embeddedAuthConfig.scope,
  apiBaseUrl: BASE_API_URL,
};

const EmbeddedAuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [tokens, setTokens] = useState<TokenSet | null>(() =>
    readInitialTokens()
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const tokensRef = useRef<TokenSet | null>(tokens);
  const refreshInFlight = useRef<Promise<string | undefined> | null>(null);

  const persist = useCallback((next: TokenSet | null) => {
    tokensRef.current = next;
    setTokens(next);
    if (next) {
      localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(next));
    } else {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    tokensRef.current = tokens;
  }, [tokens]);

  useEffect(() => {
    const hashToken = readHashToken();
    if (hashToken) {
      persist(hashToken);
      window.history.replaceState(
        null,
        '',
        window.location.pathname + window.location.search
      );
    }
  }, [persist]);

  const runOperation = useCallback(
    async <T,>(operation: () => Promise<T>): Promise<T> => {
      setIsLoading(true);
      setError(null);
      try {
        return await operation();
      } catch (err) {
        const normalized =
          err instanceof Error ? err : new Error('Authentication failed');
        setError(normalized);
        throw normalized;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const start = useCallback(
    (email: string) => runOperation(() => startPasswordless(config, email)),
    [runOperation]
  );

  const verifyOtp = useCallback(
    (email: string, otp: string) =>
      runOperation(async () => {
        const next = await verifyPasswordlessOtp(config, email, otp);
        persist(next);
      }),
    [persist, runOperation]
  );

  const loginWithConnection = useCallback(
    (connection: string) =>
      runOperation(async () => {
        const { verifier, challenge } = await createPkcePair();
        const state = randomString(16);
        const nonce = randomString(16);
        const redirectUri = `${window.location.origin}/callback`;
        const transaction: RedirectTransaction = {
          verifier,
          state,
          nonce,
          connection,
          redirectUri,
        };
        sessionStorage.setItem(
          TRANSACTION_STORAGE_KEY,
          JSON.stringify(transaction)
        );
        window.location.assign(
          buildAuthorizeUrl(config, {
            connection,
            redirectUri,
            state,
            nonce,
            codeChallenge: challenge,
          })
        );
      }),
    [runOperation]
  );

  const handleRedirectCallback = useCallback(
    () =>
      runOperation(async () => {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const returnedState = params.get('state');
        const raw = sessionStorage.getItem(TRANSACTION_STORAGE_KEY);
        if (!code || !raw) {
          throw new Error('Missing authorization code or transaction');
        }
        const transaction = JSON.parse(raw) as RedirectTransaction;
        if (returnedState !== transaction.state) {
          throw new Error('State mismatch on authorization callback');
        }
        const next = await exchangeAuthorizationCode(config, {
          code,
          redirectUri: transaction.redirectUri,
          codeVerifier: transaction.verifier,
        });
        if (next.idToken) {
          const claims = decodeJwtClaims(next.idToken);
          if (!claims || claims.nonce !== transaction.nonce) {
            throw new Error('Nonce mismatch on authorization callback');
          }
        }
        sessionStorage.removeItem(TRANSACTION_STORAGE_KEY);
        persist(next);
      }),
    [persist, runOperation]
  );

  const getAccessToken = useCallback(async (): Promise<string | undefined> => {
    const current = tokensRef.current;
    if (!current) return undefined;
    if (current.expiresAt - Date.now() > EXPIRY_LEEWAY_MS) {
      return current.accessToken;
    }
    if (!current.refreshToken) {
      persist(null);
      return undefined;
    }
    if (refreshInFlight.current) {
      return refreshInFlight.current;
    }
    const { refreshToken, source } = current;
    refreshInFlight.current = (async () => {
      try {
        const next =
          source === 'enterprise'
            ? await refreshEnterpriseTokens(config, refreshToken)
            : await refreshPasswordlessTokens(config, refreshToken);
        persist(next);
        return next.accessToken;
      } catch (err) {
        persist(null);
        throw err;
      } finally {
        refreshInFlight.current = null;
      }
    })();
    return refreshInFlight.current;
  }, [persist]);

  const logout = useCallback(
    (options?: { returnTo?: string }) => {
      persist(null);
      sessionStorage.removeItem(TRANSACTION_STORAGE_KEY);
      const returnTo = options?.returnTo || window.location.origin;
      window.location.assign(buildLogoutUrl(config, returnTo));
    },
    [persist]
  );

  const value: EmbeddedAuthContextValue = {
    isAuthenticated: Boolean(tokens),
    isLoading,
    error,
    startPasswordless: start,
    verifyOtp,
    loginWithConnection,
    handleRedirectCallback,
    getAccessToken,
    logout,
  };

  return (
    <EmbeddedAuthContext.Provider value={value}>
      {children}
    </EmbeddedAuthContext.Provider>
  );
};

export default EmbeddedAuthProvider;
